// NOT-130 — Overrides Viewer
//
// Single-pane table of every active tax override across the tenant:
//   - tax_code level (federal/state/provincial/county/city) — rate
//     and/or applies-to set on a TaxCode row
//   - tax_zone_index level (zip) — rate and/or applies-to set on a
//     TaxZoneIndex row (NOT-130 introduced this surface)
//
// One-click Revert per row dispatches to the right endpoint:
//   - tax-code rate    → POST /tax-codes/:id/restore-rate
//   - tax-code applies → PATCH /tax-codes/:id/applies-to-override { override: null }
//   - zip rate+applies → PATCH /tax-zones/:zip/override { both: null }
// The row vanishes on success because the GET /tax-zones/overrides query
// re-runs and filters it out.

import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Search, Undo2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { Fullbleed } from '@/contexts/fullbleed';
import { EntityIndexHeader } from '@/components/layouts';
import {
  useTaxOverrides,
  type TaxOverrideLevel,
  type TaxOverrideRow,
} from '@/hooks/taxes/useTaxOverrides';
import { useRestoreTaxCodeRate } from '@/hooks/taxes/useRestoreTaxCodeRate';
import { useSetAppliesToOverride } from '@/hooks/taxes/useSetAppliesToOverride';
import { useSetZoneOverride } from '@/hooks/taxes/useSetZoneOverride';
import {
  formatRate,
  appliesToLabel,
  titleCase,
} from '@/components/organisms/taxes/support/tax-helpers';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

// ─── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/taxes/overrides')({
  component: TaxOverridesPage,
});

// ─── Level filter ────────────────────────────────────────────────────────────

type LevelFilter = 'all' | TaxOverrideLevel;

const LEVEL_CHIPS: Array<{ value: LevelFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'provincial', label: 'Provincial' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'zip', label: 'ZIP' },
];

function levelBadgeClass(level: TaxOverrideLevel): string {
  switch (level) {
    case 'federal':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'state':
    case 'provincial':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'county':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    case 'city':
      return 'bg-teal-50 text-teal-700 border-teal-200';
    case 'zip':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return '';
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

function TaxOverridesPage() {
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');

  const { data, isLoading } = useTaxOverrides();
  // Memoize the array reference so downstream filter useMemo stays
  // stable across renders when the server payload didn't actually change.
  const overrides = useMemo(() => data?.overrides ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return overrides.filter((row) => {
      if (levelFilter !== 'all' && row.level !== levelFilter) return false;
      if (!q) return true;
      return (
        row.jurisdiction.toLowerCase().includes(q) ||
        (row.taxCodeName?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [overrides, search, levelFilter]);

  return (
    <>
      <Fullbleed />
      <div className="page-enter flex h-full min-h-0 flex-col gap-3">
        <div className="flex-1 min-h-0 rounded-xl border bg-card flex flex-col overflow-hidden shadow-sm">
          <EntityIndexHeader
            title={
              <div className="flex items-center gap-3 min-w-0">
                <Button asChild variant="ghost" size="icon-sm" title="Back to Taxes">
                  <Link to="/dashboard/taxes">
                    <ArrowLeft size={14} />
                  </Link>
                </Button>
                <h1 className="text-base font-semibold">Tax Overrides</h1>
                <Badge variant="outline" className="text-[10px]">
                  {overrides.length} active
                </Badge>
              </div>
            }
          />

          {/* Filter bar */}
          <div className="border-b px-4 py-3 shrink-0 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              {LEVEL_CHIPS.map((chip) => (
                <Button
                  key={chip.value}
                  variant={levelFilter === chip.value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setLevelFilter(chip.value)}
                  data-testid={`override-level-chip-${chip.value}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>
            <div className="relative ml-auto w-full max-w-xs">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search jurisdiction or tax code…"
                className="pl-7 h-7 text-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 min-h-0 overflow-auto">
            {isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {overrides.length === 0
                  ? 'No active overrides for this tenant.'
                  : 'No overrides match the current filters.'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-wide">Level</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide">
                      Jurisdiction
                    </TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide">Tax Code</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide text-right">
                      Original
                    </TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide text-right">
                      Override
                    </TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide">
                      Applies To
                    </TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wide text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <OverrideRow key={`${row.level}:${row.id}`} row={row} />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function OverrideRow({ row }: { row: TaxOverrideRow }) {
  const qc = useQueryClient();
  const restoreRate = useRestoreTaxCodeRate();
  const setAppliesToOverride = useSetAppliesToOverride();
  const setZoneOverride = useSetZoneOverride();

  const isReverting =
    restoreRate.isPending || setAppliesToOverride.isPending || setZoneOverride.isPending;

  function handleRevert() {
    if (row.level === 'zip') {
      // ZIP-level revert clears BOTH dimensions in one call. The
      // mutation hook handles the toast + cache invalidation.
      setZoneOverride.mutate(
        {
          zipCode: row.jurisdiction,
          rateOverride: null,
          appliesToOverride: null,
        },
        {
          onSuccess: () => {
            void qc.invalidateQueries({ queryKey: taxZoneKeys.overrides() });
          },
        },
      );
      return;
    }

    // tax_code level: revert each dimension that's overridden. We fire
    // both calls in parallel; whichever finishes last triggers the cache
    // invalidate for the viewer's row to vanish.
    const promises: Promise<unknown>[] = [];
    if (row.rateOverride != null) {
      promises.push(
        new Promise((resolve, reject) => {
          restoreRate.mutate(row.id, {
            onSuccess: () => resolve(undefined),
            onError: reject,
          });
        }),
      );
    }
    if (row.appliesToOverride != null) {
      promises.push(
        new Promise((resolve, reject) => {
          setAppliesToOverride.mutate(
            { id: row.id, override: null },
            { onSuccess: () => resolve(undefined), onError: reject },
          );
        }),
      );
    }
    if (promises.length === 0) {
      toast.info('Nothing to revert on this row.');
      return;
    }
    void Promise.allSettled(promises).then(() => {
      void qc.invalidateQueries({ queryKey: taxZoneKeys.overrides() });
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    });
  }

  const formatPercent = (n: number | null): string => (n == null ? '—' : formatRate(n));

  const appliesToCell = (() => {
    const original = row.originalAppliesTo;
    const override = row.appliesToOverride;
    if (override == null) return appliesToLabel(original);
    return (
      <span className="flex items-center gap-1.5 flex-wrap">
        <span className="text-muted-foreground line-through text-xs">
          {appliesToLabel(original)}
        </span>
        <span className="text-amber-700 font-medium">{appliesToLabel(override)}</span>
      </span>
    );
  })();

  return (
    <TableRow data-testid={`override-row-${row.level}-${row.id}`}>
      <TableCell>
        <Badge variant="outline" className={`text-[10px] ${levelBadgeClass(row.level)}`}>
          {titleCase(row.level)}
        </Badge>
      </TableCell>
      <TableCell className="font-mono text-xs">{row.jurisdiction}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{row.taxCodeName ?? '—'}</TableCell>
      <TableCell className="text-right tabular-nums text-xs">
        {formatPercent(row.originalRate)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-xs font-semibold text-amber-700">
        {formatPercent(row.rateOverride)}
      </TableCell>
      <TableCell className="text-xs">{appliesToCell}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1 text-amber-700 hover:text-amber-900"
          onClick={handleRevert}
          disabled={isReverting}
          data-testid={`override-revert-${row.level}-${row.id}`}
        >
          <Undo2 className="h-3 w-3" />
          {isReverting ? 'Reverting…' : 'Revert'}
        </Button>
      </TableCell>
    </TableRow>
  );
}
